import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { PageHeading } from '@/components/page-heading'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { ClassTypeahead, type ClassOption } from '@/components/ClassTypeahead'
import {
  EntryLabelPrintControl,
  toLabelPrintEntry,
} from '@/components/entries/EntryLabelPrintControl'
import { useCurrentShow } from '@/context/current-show'
import { useLabelPrinter, useLabelPrinterDiscovery } from '@/context/label-printer'
import { useActorPermissions, formatRoleList } from '@/hooks/useActorPermissions'
import { useEntryIntakeManager } from '@/hooks/useEntryIntake'
import {
  useDivisions,
  useEntry,
  useExhibitors,
  useMarkEntryNotEntered,
  useUpdateEntry,
} from '@/api/hooks'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'
import { LabelPrinterToolbar } from '@/components/label-printer-toolbar'
import { LabelPrintingDisabledNotice } from '@/components/label-printing-disabled-notice'
import { showAllowsLabelPrinting } from '@/lib/label/permissions'
import { entryInCompetition } from '@schemas'
import type { DivisionWithClasses } from '@/api/hooks'

type RouteParams = {
  entryId?: string
}

export default function EntryDetailPage() {
  const { entryId } = useParams<RouteParams>()
  const { currentShow } = useCurrentShow()
  const currentSlug = useShowSlug()
  const intake = useEntryIntakeManager(currentShow?.id)

  const entryQuery = useEntry(entryId)
  const updateEntry = useUpdateEntry()
  const markNotEntered = useMarkEntryNotEntered()
  const divisionsQuery = useDivisions(currentShow?.id)
  const permissions = useActorPermissions(currentShow?.id)

  const entry = entryQuery.data ?? null
  const labelPrinter = useLabelPrinter()
  // The entry carries a snapshot of its show. The selected show is live when it is the same one.
  const liveShow = currentShow && currentShow.id === entry?.show.id ? currentShow : null
  const showStatusForPrinting =
    liveShow?.status ?? entry?.show?.status ?? currentShow?.status ?? null
  const labelPrintingDisabled =
    liveShow?.labelPrintingDisabled ??
    entry?.show?.labelPrintingDisabled ??
    currentShow?.labelPrintingDisabled ??
    false
  const allowsLabelPrinting = showAllowsLabelPrinting({
    status: showStatusForPrinting,
    labelPrintingDisabled,
  })
  useLabelPrinterDiscovery(allowsLabelPrinting)
  const [markDialogOpen, setMarkDialogOpen] = useState(false)
  const [markReason, setMarkReason] = useState('')

  const [pendingClassId, setPendingClassId] = useState<string>('')

  useEffect(() => {
    if (entry?.classId) {
      setPendingClassId(entry.classId)
    } else {
      setPendingClassId('')
    }
  }, [entry?.classId])

  // A person keeps one active entry per class across their entrant records, so classes they
  // already hold can't be chosen. This is the entrant list check-in reads.
  const entrantsQuery = useExhibitors(
    { showId: entry?.showId, limit: 500 },
    { enabled: entry?.show.status === 'accepting_entries' && entry.status !== 'not_entered' }
  )
  const currentEntryId = entry?.id
  const entrantId = entry?.exhibitorId
  const personId = entry?.exhibitor?.person?.id
  const classOptions = useMemo<ClassOption[]>(() => {
    const divisions = divisionsQuery.data ?? []
    const held = new Map<string, string>()
    for (const record of entrantsQuery.data ?? []) {
      const samePerson = personId && (record.personId ?? record.person?.id) === personId
      if (record.id !== entrantId && !samePerson) continue
      for (const other of record.entries ?? []) {
        const classId = other?.class?.id
        if (
          classId &&
          other.id !== currentEntryId &&
          entryInCompetition(other.status) &&
          !held.has(classId)
        )
          held.set(classId, other.entryCode)
      }
    }
    return divisions
      .flatMap(div => buildClassOptionsForDivision(div))
      .map(option => {
        const code = held.get(option.id)
        return code
          ? { ...option, disabled: true, statusLabel: `Already entered · Exhibit ID ${code}` }
          : option
      })
  }, [currentEntryId, divisionsQuery.data, entrantsQuery.data, entrantId, personId])

  const isNotEntered = entry?.status === 'not_entered'
  const permissionLoading = permissions.staffQuery?.isLoading ?? false
  const canManageEntries = permissions.can('entries:write')
  const requiredRoles = useMemo(
    () => formatRoleList(permissions.getAllowedRoles('entries:write')),
    [permissions]
  )

  const classChangeDisabledReason = useMemo(() => {
    if (!entryId) return 'Missing entry identifier.'
    if (permissionLoading) return 'Loading permissions…'
    if (!entry) return entryQuery.isLoading ? 'Loading entry…' : undefined
    if (!canManageEntries) {
      return `Changing classes requires ${requiredRoles}.`
    }
    if (entry.show.labelOnlyMode) {
      return 'Label-only mode is enabled for this show.'
    }
    if (entry.show.status !== 'accepting_entries') {
      return 'Class changes are available while the show is accepting entries.'
    }
    if (isNotEntered) {
      return 'This entry has been marked as Not Entered. Class changes are locked.'
    }
    if (entry.status === 'judged' || entry.status === 'finalized') {
      return 'This entry has been judged or finalized. Class changes are locked.'
    }
    if (entry.status === 'disqualified') {
      return 'This entry was disqualified. Class changes are locked.'
    }
    if (divisionsQuery.isError) {
      return 'Unable to load classes for this show.'
    }
    return undefined
  }, [
    canManageEntries,
    entry,
    entryId,
    entryQuery.isLoading,
    divisionsQuery.isError,
    permissionLoading,
    requiredRoles,
    isNotEntered,
  ])

  const classChangeDisabled =
    Boolean(classChangeDisabledReason) || updateEntry.isPending || !entry || !entryId
  const entrantFinalized = Boolean(entry?.exhibitor?.finalizedAt)
  const entryLocked = entry ? ['judged', 'finalized', 'disqualified'].includes(entry.status) : false
  const showAllowsNotEntered = entry
    ? ['accepting_entries', 'judging', 'finalized'].includes(entry.show.status)
    : false

  const markDisabledReason = useMemo(() => {
    if (!entryId) return 'Missing entry identifier.'
    if (!entry) return entryQuery.isLoading ? 'Loading entry…' : undefined
    if (!canManageEntries) {
      return `Marking entries Not Entered requires ${requiredRoles}.`
    }
    if (!showAllowsNotEntered) {
      return 'Marking Not Entered is available once the show is accepting entries.'
    }
    if (entry.status === 'disqualified') {
      return 'This entry was disqualified. Not Entered is unavailable.'
    }
    if (!entrantFinalized) {
      return 'Finalize this entrant before marking entries as Not Entered.'
    }
    if (entryLocked) {
      return 'This entry has been judged or finalized. Not Entered is unavailable.'
    }
    return undefined
  }, [
    canManageEntries,
    entrantFinalized,
    entry,
    entryId,
    entryLocked,
    entryQuery.isLoading,
    requiredRoles,
    showAllowsNotEntered,
  ])

  const markButtonDisabled = markNotEntered.isPending || Boolean(markDisabledReason)

  const notEnteredAtFormatted = useMemo(
    () => formatTimestamp(entry?.notEnteredAt),
    [entry?.notEnteredAt]
  )
  const disqualifiedAtFormatted = useMemo(
    () => formatTimestamp(entry?.disqualifiedAt),
    [entry?.disqualifiedAt]
  )

  const notEnteredByRoleLabel = useMemo(() => {
    if (!entry?.notEnteredByRole) return null
    return formatRoleList([entry.notEnteredByRole])
  }, [entry?.notEnteredByRole])

  const hasPendingChange = entry && pendingClassId && pendingClassId !== entry.classId

  const entrantDetailPath =
    entry?.exhibitor?.id && currentSlug
      ? showAware(`/entrants/${entry.exhibitor.id}`, { currentSlug })
      : null

  const backToEntriesPath =
    currentSlug !== null ? showAware('/entries', { currentSlug }) : '/entries'

  const handleClassSelect = (nextClassId: string) => {
    setPendingClassId(nextClassId)
  }

  const handleSave = async () => {
    if (!entry || !entryId || !hasPendingChange) return
    try {
      await updateEntry.mutateAsync({ entryId, payload: { classId: pendingClassId } })
      toast.success('Class updated')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to update entry. Please try again.'
      toast.error(message)
    }
  }

  const handleMarkDialogOpenChange = (open: boolean) => {
    if (!open) {
      setMarkReason('')
    }
    setMarkDialogOpen(open)
  }

  const handleConfirmMarkNotEntered = async () => {
    if (!entry || !entryId) return
    const trimmed = markReason.trim()
    try {
      await markNotEntered.mutateAsync({
        entryId,
        payload: trimmed.length > 0 ? { reason: trimmed } : undefined,
      })
      toast.success('Entry marked as Not Entered')
      setMarkDialogOpen(false)
      setMarkReason('')
      await intake.removeCheckedInExhibit(entry.id, entry.exhibit?.id ?? null)
      await intake.removeRecentEntry(entry.id, entry.entryCode ?? null)
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to mark entry as Not Entered. Please try again.'
      toast.error(message)
    }
  }

  if (entryQuery.isLoading) {
    return (
      <div className="space-y-6">
        <PageHeading runLevel={2}>Entry</PageHeading>
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    )
  }

  if (!entry) {
    return (
      <div className="space-y-6">
        <PageHeading runLevel={2}>Entry</PageHeading>
        <Card>
          <CardHeader>
            <CardTitle>Entry not found</CardTitle>
            <CardDescription>
              We couldn’t find the requested entry. It may have been deleted or you may not have
              access.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button asChild variant="outline">
              <Link to={backToEntriesPath}>Back to entries</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  const statusLabel = getEntryStatusLabel(entry.status)
  const statusVariant = getStatusBadgeVariant(entry.status)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <PageHeading runLevel={2}>Entry</PageHeading>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          {allowsLabelPrinting ? (
            <LabelPrinterToolbar state={labelPrinter} className="self-start" />
          ) : null}
          {liveShow ? (
            <EntryLabelPrintControl entry={toLabelPrintEntry(entry)} show={liveShow} />
          ) : null}
          <Button variant="outline" asChild>
            <Link to={backToEntriesPath}>Back to entries</Link>
          </Button>
        </div>
      </div>

      {labelPrintingDisabled && <LabelPrintingDisabledNotice />}

      <div className="grid gap-4 lg:grid-cols-[2fr,1fr]">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>Entry Overview</CardTitle>
              <CardDescription>Review entry details and update the class.</CardDescription>
            </div>
            <Badge variant={statusVariant}>{statusLabel}</Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <DetailItem label="Exhibit ID">
                <span className="font-mono text-base">{entry.entryCode ?? 'Not assigned'}</span>
              </DetailItem>
              <DetailItem label="Current class">
                {entry.class ? (
                  <span className="text-sm text-foreground">
                    <span className="font-medium">{entry.class.code}</span>
                    {entry.class.name ? <span>{` · ${entry.class.name}`}</span> : null}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">Not assigned</span>
                )}
              </DetailItem>
              <DetailItem label="Title">
                {entry.title ? (
                  <span className="text-sm text-foreground">{entry.title}</span>
                ) : (
                  <span className="text-sm text-muted-foreground">No title provided</span>
                )}
              </DetailItem>
              <DetailItem label="Fees">
                <span className="text-sm text-foreground">${entry.fees.toFixed(2)}</span>
              </DetailItem>
            </div>

            <DetailItem label="Notes">
              {entry.notes ? (
                <p className="whitespace-pre-wrap text-sm text-foreground">{entry.notes}</p>
              ) : (
                <p className="text-sm text-muted-foreground">No notes on this entry.</p>
              )}
            </DetailItem>

            {isNotEntered ? (
              <div className="rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p className="font-medium">
                  This entry was marked as Not Entered
                  {notEnteredAtFormatted ? ` on ${notEnteredAtFormatted}` : ''}.
                </p>
                {entry.notEnteredBy || notEnteredByRoleLabel ? (
                  <p className="mt-1">
                    Marked by {entry.notEnteredBy ?? 'staff'}
                    {notEnteredByRoleLabel ? ` (${notEnteredByRoleLabel})` : ''}.
                  </p>
                ) : null}
                {entry.notEnteredReason ? (
                  <p className="mt-2 whitespace-pre-wrap">{entry.notEnteredReason}</p>
                ) : null}
              </div>
            ) : null}

            {entry.status === 'disqualified' ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
                <p className="font-medium">
                  This entry was disqualified
                  {disqualifiedAtFormatted ? ` on ${disqualifiedAtFormatted}` : ''}.
                </p>
                {entry.disqualifiedReason ? (
                  <p className="mt-2 whitespace-pre-wrap">{entry.disqualifiedReason}</p>
                ) : null}
              </div>
            ) : null}

            {entry.status === 'checked_in' ? (
              <div className="rounded-md border border-border/70 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                Labels may need to be reprinted after changing the class.
              </div>
            ) : null}

            <div className="space-y-2">
              <label htmlFor="entry-class-typeahead" className="text-sm font-medium">
                Change class
              </label>
              <ClassTypeahead
                id="entry-class-typeahead"
                value={pendingClassId}
                onChange={handleClassSelect}
                options={classOptions}
                disabled={classChangeDisabled}
                isLoading={divisionsQuery.isLoading}
                placeholder={
                  divisionsQuery.isLoading ? 'Loading classes…' : 'Search classes by code or name…'
                }
                emptyText={
                  divisionsQuery.isError
                    ? 'Unable to load classes.'
                    : 'No classes match your search.'
                }
              />
              {classChangeDisabledReason ? (
                <p className="text-xs text-muted-foreground">{classChangeDisabledReason}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Search by class code or name. Changes take effect immediately after saving.
                </p>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-muted-foreground">
                Last updated{' '}
                {new Date(entry.updatedAt ?? entry.createdAt ?? Date.now()).toLocaleString()}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  disabled={!hasPendingChange || updateEntry.isPending}
                  onClick={() => setPendingClassId(entry.classId)}
                >
                  Reset
                </Button>
                <Button
                  disabled={classChangeDisabled || !hasPendingChange}
                  onClick={() => {
                    void handleSave()
                  }}
                >
                  {updateEntry.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
            {!isNotEntered ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  Mark an entry as Not Entered when an exhibitor does not deliver the exhibit. The
                  Exhibit ID stays reserved for auditing.
                </p>
                <Dialog open={markDialogOpen} onOpenChange={handleMarkDialogOpenChange}>
                  <DialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={markButtonDisabled}
                      title={markDisabledReason ?? undefined}
                    >
                      Mark as Not Entered
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader className="space-y-1">
                      <DialogTitle>Mark entry as Not Entered</DialogTitle>
                      <DialogDescription>
                        This removes the entry from judging, awards, and reports. The Exhibit ID
                        remains reserved for audit history.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <label htmlFor="not-entered-reason" className="text-sm font-medium">
                          Reason (optional)
                        </label>
                        <Textarea
                          id="not-entered-reason"
                          value={markReason}
                          onChange={event => setMarkReason(event.target.value)}
                          placeholder="Add a short note for the audit log…"
                          maxLength={500}
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground">
                          Notes are optional and stored in the audit trail.
                        </p>
                      </div>
                    </div>
                    <DialogFooter className="gap-2 sm:justify-end">
                      <Button
                        variant="ghost"
                        onClick={() => handleMarkDialogOpenChange(false)}
                        disabled={markNotEntered.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          void handleConfirmMarkNotEntered()
                        }}
                        disabled={markNotEntered.isPending}
                      >
                        {markNotEntered.isPending ? 'Marking…' : 'Mark as Not Entered'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            ) : null}
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Entrant</CardTitle>
            <CardDescription>Contact information for this entry&apos;s exhibitor.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm font-medium">
                {entry.exhibitor?.name ?? entry.exhibitor?.person?.name ?? 'Unknown entrant'}
              </p>
              <div className="mt-1 space-y-1 text-xs text-muted-foreground">
                <div>{entry.exhibitor?.email ?? 'No email on file'}</div>
                <div>{entry.exhibitor?.phone ?? 'No phone on file'}</div>
                {entry.exhibitor?.entrantCode ? (
                  <div>
                    Entrant code:{' '}
                    <span className="font-mono text-muted-foreground">
                      {entry.exhibitor.entrantCode}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
            <Separator />
            <div className="flex flex-col gap-2 text-xs text-muted-foreground">
              <div>
                Show status:{' '}
                <span className="font-medium text-foreground">
                  {getShowStatusLabel(entry.show.status)}
                </span>
              </div>
              <div>
                Label-only mode:{' '}
                <span className="font-medium text-foreground">
                  {entry.show.labelOnlyMode ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            {entrantDetailPath ? (
              <Button asChild variant="outline" size="sm">
                <Link to={entrantDetailPath}>View entrant</Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled>
                Entrant view unavailable
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}

function DetailItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      <div>{children}</div>
    </div>
  )
}

function buildClassOptionsForDivision(division: DivisionWithClasses | undefined): ClassOption[] {
  if (!division || !Array.isArray(division.classes)) return []
  return division.classes
    .filter((cls): cls is DivisionWithClasses['classes'][number] & { id: string } =>
      Boolean(cls?.id)
    )
    .map(cls => ({
      id: cls.id,
      code: cls.code,
      name: cls.name,
      label: `${cls.code} · ${cls.name}`,
      divisionName: division.name ?? null,
      statusLabel: null,
    }))
}

function formatTimestamp(value: string | null | undefined) {
  if (!value) return null
  const timestamp = Date.parse(value)
  if (Number.isNaN(timestamp)) return null
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(timestamp)
  )
}

function getEntryStatusLabel(status: string | undefined) {
  switch (status) {
    case 'draft':
      return 'Draft'
    case 'checked_in':
      return 'Checked in'
    case 'not_entered':
      return 'Not Entered'
    case 'judged':
      return 'Judged'
    case 'finalized':
      return 'Finalized'
    case 'disqualified':
      return 'Disqualified'
    default:
      return 'Unknown'
  }
}

function getStatusBadgeVariant(
  status: string | undefined
): 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'draft':
      return 'outline'
    case 'checked_in':
      return 'secondary'
    case 'not_entered':
      return 'outline'
    case 'judged':
    case 'finalized':
      return 'secondary'
    case 'disqualified':
      return 'destructive'
    default:
      return 'outline'
  }
}

function getShowStatusLabel(status: string | undefined) {
  switch (status) {
    case 'draft':
      return 'Draft'
    case 'accepting_entries':
      return 'Accepting entries'
    case 'judging':
      return 'Judging'
    case 'finalized':
      return 'Finalized'
    default:
      return 'Unknown'
  }
}
